/* import __COLOCATED_TEMPLATE__ from './language-selector.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */

import type AppService from 'embercom/services/app-service';
import type Store from '@ember-data/store';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import type IntlService from 'ember-intl/services/intl';
import { type Language } from 'embercom/lib/ai-content-library/constants';
import { FIN_SUPPORTED_LANGUAGES } from 'embercom/lib/ai-content-library/constants';
import { action } from '@ember/object';
import type FinSetupLocalizationService from 'embercom/services/fin-setup-localization-service';

interface DropDownOption {
  text: string;
  value: string;
  isDisabled?: boolean;
  isLoading?: boolean;
}

export interface LanguageSelectorArgs {
  onChange: (locale: string | null) => void;
  selectedLocale?: string;
  disabled?: boolean;
  disableAutoLanguageDetection?: boolean;
  isInModal?: boolean;
}

export default class LanguageSelector extends Component<LanguageSelectorArgs> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare finSetupLocalizationService: FinSetupLocalizationService;
  @tracked baseLanguages: Language[] = [];

  constructor(owner: unknown, args: any) {
    super(owner, args);
    taskFor(this.initialiseSupportedLanguages).perform();
  }

  willDestroy() {
    super.willDestroy();
    this.finSetupLocalizationService.stopPolling();
  }

  get app() {
    return this.appService.app;
  }

  get isLoading() {
    return taskFor(this.initialiseSupportedLanguages).isRunning;
  }

  get selectedLocale() {
    return this.args.selectedLocale || 'en';
  }

  get supportedLanguages(): DropDownOption[] {
    return this.baseLanguages.map((language: Language) => {
      let isDisabled = this.finSetupLocalizationService.isLocaleDisabled(language.locale);
      let isLoading = this.finSetupLocalizationService.isLocaleLoading(language.locale);

      return {
        text: language.name,
        value: language.locale,
        isDisabled,
        isLoading,
      };
    });
  }

  @task({ drop: true })
  *initialiseSupportedLanguages(): TaskGenerator<void> {
    let languageSettings = yield this.store.findRecord(
      'messenger-settings/languages',
      this.appService.app.id,
    );
    let nonDefaultPermittedLocales = languageSettings.supportedLocales
      .filter((locale: any) => locale.isPermitted)
      .map((locale: any) => locale.localeId);
    let messengerSupportedLocales = [
      'en',
      languageSettings.defaultLocale,
      ...nonDefaultPermittedLocales,
    ].uniq();

    // Intersection of Fin supported locales and Messenger supported locales
    this.baseLanguages = FIN_SUPPORTED_LANGUAGES.filter((language: Language) =>
      messengerSupportedLocales.includes(language.locale),
    );
  }

  @action
  startPollingIfNeeded() {
    this.finSetupLocalizationService.startPolling();
  }

  @action
  onLocaleClick(locale: string) {
    // Check if the locale is disabled due to pending translation
    let isDisabled = this.finSetupLocalizationService.isLocaleDisabled(locale);

    if (isDisabled) {
      // Don't allow selection of disabled locales
      return;
    }

    this.args.onChange(locale);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::Preview::Controls::LanguageSelector': typeof LanguageSelector;
    'workflows/preview/controls/language-selector': typeof LanguageSelector;
  }
}
